import { useCallback } from 'react'
import { IServiceProduct } from '@user/store/serviceProducts/typings'
import { IServiceFrequency } from '@user/store/serviceFrequency/typings'
import { ImportUtilityModel } from '../../AddImportUtilityDrawer/typings'
import { ImportEntityType } from '../../typings'

export function useService(
  allServices: Map<string, IServiceProduct[]> | null,
  allServiceFrequencies: IServiceFrequency[],
  importSettings: ImportUtilityModel | null,
) {
  const assignServiceId = useCallback(
    (record) => {
      const newRecord = { ...record }
      const { serviceName, serviceId, branchId } = newRecord.row

      if (!serviceName || !branchId?.value || !allServices)
        return { ...newRecord, serviceId: { ...serviceId, value: '' } }

      serviceId.value =
        allServices
          .get(branchId.value)
          ?.find(
            (x) =>
              `${x.name}${x.description ? ' - ' + x.description : ''}` ===
              serviceName.value,
          )?.id ?? ''

      return newRecord
    },
    [allServices],
  )

  const assignServicesForBranch = useCallback(
    (record) => {
      const newRecord = { ...record }

      if (importSettings?.entityType === ImportEntityType.DraftLead)
        return newRecord

      const { serviceName, branchId } = newRecord.row

      if (!serviceName || !allServices) return newRecord

      serviceName.selectOptions = branchId?.value
        ? allServices.get(branchId.value)?.map((s) => {
            const value = s.description
              ? `${s.name} - ${s.description}`
              : s.name
            return { label: value, value: value }
          })
        : []

      return newRecord
    },
    [allServices, importSettings],
  )

  const assignDefaultsForServiceFields = useCallback(
    (record) => {
      const newRecord = { ...record }

      if (
        importSettings?.entityType === ImportEntityType.DraftLead ||
        !newRecord.row.serviceName
      )
        return newRecord

      const {
        serviceId,
        frequencyName,
        frequencyId,
        branchId,
        initialPrice,
        recurringPrice,
        taxable,
        annualOccurrences,
      } = newRecord.row

      if (branchId && serviceId && serviceId.value) {
        const service = allServices
          ?.get(branchId.value)
          ?.find((x) => x.id === serviceId.value)

        const servFrequency = allServiceFrequencies.find(
          (x) => x.name === service?.frequency || x.id === frequencyId?.value,
        )

        if (frequencyName && !frequencyName.value && servFrequency) {
          frequencyName.value = `${servFrequency?.annualOccurrences} - ${servFrequency?.description}`
          frequencyId.value = servFrequency.id
          annualOccurrences.value = servFrequency.annualOccurrences
        }

        const validService = !!(serviceId.value && frequencyId?.value)

        initialPrice.value = initialPrice.value
          ? initialPrice.value
          : serviceId.value
          ? service?.initialPrice
          : ''
        recurringPrice.value =
          recurringPrice.value ||
          (validService && servFrequency && servFrequency?.annualOccurrences > 0
            ? service?.price
            : '')

        if (service?.taxable !== undefined && !taxable.value) {
          taxable.value = service?.taxable ? 'Y' : 'N'
        }
      }

      return newRecord
    },
    [allServices, allServiceFrequencies, importSettings],
  )

  return {
    assignServiceId,
    assignServicesForBranch,
    assignDefaultsForServiceFields,
  }
}
